import { useRef } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    Compass,
    UserRoundCheck,
    BookOpen,
    ShieldCheck,
    HeartPulse,
    Bone,
    Ribbon,
    Flower2,
    FileText,
    CalendarClock,
    Plane,
} from "lucide-react";
import { LeafMark } from "@/components/LeafMark";
import { Reveal, MaskedLine, SectionHeading, EASE } from "@/components/motion";
import { Marquee } from "@/components/Marquee";
import { JourneyTimeline } from "@/components/JourneyTimeline";
import { CtaBand } from "@/components/CtaBand";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";

const ICONS = {
    compass: Compass,
    user: UserRoundCheck,
    book: BookOpen,
    shield: ShieldCheck,
    heart: HeartPulse,
    bone: Bone,
    ribbon: Ribbon,
    flower: Flower2,
    file: FileText,
    calendar: CalendarClock,
    plane: Plane,
};

const Hero = () => {
    const { t, tHtml, site: siteData } = useLang();
    const { TRUST_POINTS } = siteData;
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-5%", "9%"]);
    const contentY = useTransform(scrollYProgress, [0, 1], [0, 60]);

    return (
        <section
            ref={ref}
            data-testid="home-hero"
            className="relative min-h-[100svh] flex items-center overflow-hidden pt-[80px]"
        >
            <LeafMark className="pointer-events-none absolute -right-40 top-1/4 w-[560px] h-[560px] text-pine/[0.05] rotate-12" />

            <div className="container-site relative w-full py-14 lg:py-20">
                <div className="grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
                    <motion.div style={{ y: contentY }} className="lg:col-span-7">
                        <motion.p
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                            className="eyebrow text-moss flex items-center gap-3"
                        >
                            <LeafMark className="w-4 h-4 text-natural" />
                            <span>{t("home.hero.eyebrow", "Healthcare, Wellness & Ayurveda — coordinated.")}</span>
                            <span
                                className="hidden sm:block h-px w-16 bg-pine/25"
                                aria-hidden="true"
                            />
                        </motion.p>

                        <h1 className="mt-7 font-serif font-medium text-[2.8rem] leading-[1.05] sm:text-6xl sm:leading-[1.02] lg:text-[4.4rem] lg:leading-[1.03] tracking-tight text-forest max-w-3xl">
                            <MaskedLine load delay={0.2}>
                                {tHtml("home.hero.l1", <>Healthcare, Wellness</>)}
                            </MaskedLine>
                            <MaskedLine load delay={0.32}>
                                {tHtml("home.hero.l2", <>&amp; Ayurveda —{" "}
                                <span className="italic text-pine">beyond</span></>)}
                            </MaskedLine>
                            <MaskedLine load delay={0.44}>
                                {tHtml("home.hero.l3", <><span className="italic text-pine">borders.</span></>)}
                            </MaskedLine>
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.6, ease: EASE }}
                            className="mt-5 font-serif italic text-2xl sm:text-3xl text-pine/90 leading-snug"
                            data-testid="hero-kerala-line"
                        >
                            {t("home.hero.keralaLine", "Starting in Kerala — God's Own Country.")}
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
                            className="mt-5 text-base sm:text-lg leading-relaxed text-moss max-w-xl"
                        >
                            {t("home.hero.support", "Connect with trusted healthcare providers, wellness programs and Ayurvedic care in India, with personalised coordination from enquiry to follow-up.")}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
                            className="mt-10 flex flex-col sm:flex-row gap-4"
                        >
                            <Link
                                to="/get-a-medical-opinion"
                                data-testid="hero-cta-opinion"
                                className="btn-primary group"
                            >
                                {t("common.get_medical_opinion", "Get a Medical Opinion")}
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/why-kerala"
                                data-testid="hero-cta-kerala"
                                className="btn-secondary group"
                            >
                                {t("home.text4", "Explore Healthcare in Kerala")}
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </motion.div>

                        <motion.ul
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1, delay: 1.05 }}
                            data-testid="hero-trust-points"
                            className="mt-14 pt-6 border-t border-pine/15 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4"
                        >
                            {TRUST_POINTS.map((point) => (
                                <li
                                    key={point}
                                    className="flex items-start gap-2.5 text-[13px] sm:text-sm font-semibold text-charcoal/80"
                                >
                                    <LeafMark className="w-3.5 h-3.5 mt-0.5 text-natural shrink-0" />
                                    {point}
                                </li>
                            ))}
                        </motion.ul>
                    </motion.div>

                    <div className="lg:col-span-5 relative">
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
                            className="relative pb-12"
                        >
                            {/* Kerala identity backdrop */}
                            <div className="rounded-2xl overflow-hidden h-[340px] sm:h-[440px] lg:h-[520px] lg:ml-16 shadow-[0_30px_70px_rgba(6,60,53,0.16)] relative">
                                <motion.img
                                    style={{ y: imgY }}
                                    src={IMG.keralaArchitecture}
                                    alt="Elegant traditional Kerala architecture with warm lamps beside a courtyard pool at dusk"
                                    data-testid="hero-backdrop-image"
                                    className="absolute inset-0 w-full h-[115%] object-cover"
                                />
                                <div
                                    aria-hidden="true"
                                    className="absolute inset-0 bg-forest/10"
                                />
                            </div>

                            <motion.span
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 1.15, ease: EASE }}
                                className="absolute top-6 right-6 lg:right-24 z-10 bg-ivory/95 backdrop-blur text-forest text-[11px] font-bold tracking-widest2 uppercase px-4 py-2 rounded-lg border border-pine/10"
                                data-testid="hero-location-chip"
                            >
                                {t("home.text5", "Kerala · India")}
                            </motion.span>

                            {/* primary: Indian doctor + international patient */}
                            <motion.div
                                initial={{ clipPath: "inset(0 0 100% 0)" }}
                                animate={{ clipPath: "inset(0 0 0% 0)" }}
                                transition={{ duration: 1.2, delay: 0.8, ease: EASE }}
                                className="arch-frame absolute -bottom-2 left-0 sm:left-4 z-10 w-[62%] max-w-[310px] h-[230px] sm:h-[290px] lg:h-[310px] ring-4 ring-ivory shadow-[0_30px_60px_rgba(6,60,53,0.25)]"
                            >
                                <motion.img
                                    style={{ y: imgY }}
                                    src={IMG.hero}
                                    alt="An experienced Indian doctor warmly consulting an international patient in a sophisticated modern clinic with tropical greenery outside"
                                    data-testid="hero-primary-image"
                                    className="absolute inset-0 w-full h-[112%] object-cover"
                                />
                            </motion.div>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1, delay: 1.5 }}
                            className="mt-6 text-right font-serif italic text-xl text-moss pr-2"
                            data-testid="hero-editorial-statement"
                        >
                            {t("home.heroQuote", '"Care knows no borders."')}
                        </motion.p>
                    </div>
                </div>
            </div>
        </section>
    );
};

const ThreeWays = () => {
    const { t, tHtml, site: siteData } = useLang();
    const { THREE_WAYS } = siteData;
    return (
    <section className="section-pad bg-white" data-testid="three-ways-section">
        <div className="container-site">
            <SectionHeading
                index="01"
                eyebrow={t("home.threeWays.eyebrow", "Three Ways to Care")}
                title={tHtml("home.threeWays.title", <>
                    Healthcare, Wellness &amp;{" "}
                    <span className="italic text-pine">Ayurveda.</span>
                </>)}
                intro={t("home.threeWays.intro", "Whether you are seeking specialist medical care, a restorative wellness experience, or traditional Ayurvedic care, UniversHeal helps you navigate the right pathway in India.")}
            />
            <div className="mt-14 grid md:grid-cols-3 gap-6">
                {THREE_WAYS.map((way, i) => (
                    <Reveal key={way.title} delay={i * 0.08} className="h-full">
                        <Link
                            to={way.path}
                            data-testid={way.testid}
                            className="group h-full flex flex-col bg-ivory border border-pine/10 rounded-2xl overflow-hidden card-lift hover:border-pine/30"
                        >
                            <span className="relative block h-52 overflow-hidden">
                                <img
                                    src={IMG[way.img]}
                                    alt={way.imgAlt}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    loading="lazy"
                                />
                                <span className="absolute inset-0 bg-gradient-to-t from-forest/25 via-transparent to-transparent" />
                            </span>
                            <span className="flex flex-col flex-1 p-8">
                                <span className="font-serif font-medium text-[1.7rem] leading-tight text-forest">
                                    {way.title}
                                </span>
                                <span className="mt-3 text-sm leading-relaxed text-moss">
                                    {way.desc}
                                </span>
                                <span className="mt-5 flex-1">
                                    <span className="block border-t border-pine/10 pt-5">
                                        <span className="grid gap-2">
                                            {way.items.map((item) => (
                                                <span
                                                    key={item}
                                                    className="flex items-start gap-2.5 text-[13px] text-charcoal/75 leading-snug"
                                                >
                                                    <LeafMark className="w-3 h-3 mt-0.5 text-natural shrink-0" />
                                                    {item}
                                                </span>
                                            ))}
                                        </span>
                                    </span>
                                </span>
                                <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-forest">
                                    {way.cta}
                                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                </span>
                            </span>
                        </Link>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>

    );
};

const Difference = () => {
    const { t, tHtml, site: siteData } = useLang();
    const { DIFFERENCE_CARDS } = siteData;
    return (
    <section className="section-pad bg-white" data-testid="difference-section">
        <div className="container-site">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <div>
                    <SectionHeading
                        index="02"
                        eyebrow={t("home.difference.eyebrow", "The UniversHeal Difference")}
                        title={tHtml("home.difference.title", <>
                            More than treatment. A{" "}
                            <span className="italic text-pine">partner</span> in
                            {t("home.text6", "your healthcare journey.")}
                        </>)}
                        intro={t("home.difference.intro", "UniversHeal helps international patients navigate healthcare in India with personalized coordination and human support — so distance, language and unfamiliar systems never stand between you and good care.")}
                    />
                    <div className="mt-4">
                        {DIFFERENCE_CARDS.map((card, i) => {
                            const Icon = ICONS[card.icon];
                            return (
                                <Reveal key={card.title} delay={i * 0.06}>
                                    <div
                                        data-testid={`difference-item-${i + 1}`}
                                        className="flex items-start gap-5 py-6 border-t border-pine/10"
                                    >
                                        <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-sage text-forest shrink-0">
                                            <Icon className="w-5 h-5" strokeWidth={1.5} />
                                        </span>
                                        <div>
                                            <h3 className="font-serif font-medium text-[1.35rem] leading-snug text-forest">
                                                {card.title}
                                            </h3>
                                            <p className="mt-1.5 text-sm leading-relaxed text-moss">
                                                {card.desc}
                                            </p>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
                <Reveal delay={0.1} className="lg:pl-6">
                    <div className="relative">
                        <div className="rounded-2xl overflow-hidden h-[420px] sm:h-[540px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                            <img
                                src={IMG.difference}
                                alt="An Indian senior doctor warmly discussing a medical report with an international patient"
                                data-testid="difference-image"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                        <div className="absolute -bottom-6 right-6 sm:right-10 bg-forest text-ivory rounded-2xl px-6 py-5 shadow-xl max-w-[240px]">
                            <p className="font-serif italic text-lg leading-snug">
                                {t("home.placesHope", '"Different places. A common hope."')}
                            </p>
                        </div>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>

    );
};

const HowItWorks = () => {
    const { t, tHtml, site: siteData } = useLang();
    const { HOW_IT_WORKS } = siteData;
    return (
    <section
        className="section-pad bg-forest text-ivory relative overflow-hidden"
        data-testid="how-it-works-section"
    >
        <div
            aria-hidden="true"
            className="pointer-events-none absolute -left-40 bottom-0 w-[600px] h-[600px] rounded-full bg-pine/50 blur-3xl"
        />
        <div className="container-site relative">
            <SectionHeading
                index="03"
                eyebrow={t("home.howItWorks.eyebrow", "How It Works")}
                dark
                title={t("home.howItWorks.title", "Your healthcare journey, made simpler.")}
                aside={
                    <Link
                        to="/how-it-works"
                        data-testid="how-it-works-link"
                        className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-natural hover:text-ivory transition-colors"
                    >
                        {t("home.howItWorks.link", "See the full process")}
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                }
            />
            <div className="relative mt-16">
                <div
                    aria-hidden="true"
                    className="absolute top-6 left-[10%] right-[10%] hidden lg:block h-px bg-gradient-to-r from-transparent via-natural/40 to-transparent"
                />
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
                    {HOW_IT_WORKS.map((step, i) => {
                        const Icon = ICONS[step.icon];
                        return (
                            <Reveal key={step.number} delay={i * 0.1} className="h-full">
                                <div
                                    data-testid={`how-step-${i + 1}`}
                                    className="h-full relative"
                                >
                                    <span className="relative z-10 flex items-center justify-center w-12 h-12 rounded-xl bg-ivory/10 border border-natural/40 text-natural">
                                        <Icon className="w-5 h-5" strokeWidth={1.5} />
                                    </span>
                                    <p className="mt-6 font-serif italic text-2xl text-natural leading-none">
                                        {step.number}
                                    </p>
                                    <h3 className="mt-3 font-serif font-medium text-[1.55rem] leading-snug">
                                        {step.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-ivory/65">
                                        {step.desc}
                                    </p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
            <Reveal delay={0.2}>
                <div className="mt-16">
                    <Link
                        to="/get-a-medical-opinion"
                        data-testid="how-it-works-cta"
                        className="btn-ivory group"
                    >
                        {t("home.howItWorks.cta", "Start Your Journey")}
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </div>
            </Reveal>
        </div>
    </section>

    );
};

const Specialties = () => {
    const { t, tHtml, site: siteData } = useLang();
    const { SPECIALTIES } = siteData;
    return (
    <section className="section-pad bg-white" data-testid="specialties-section">
        <div className="container-site">
            <SectionHeading
                index="04"
                eyebrow={t("home.specialties.eyebrow", "Specialties")}
                title={tHtml("home.specialties.title", <>
                    Specialized care.{" "}
                    <span className="italic text-pine">Personal</span> attention.
                </>)}
                intro={t("home.specialties.intro", "Our initial focus areas at launch. Specialist availability is confirmed case by case, based on your needs.")}
            />
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {SPECIALTIES.map((spec, i) => {
                    const Icon = ICONS[spec.icon];
                    return (
                        <Reveal key={spec.slug} delay={i * 0.07} className="h-full">
                            <Link
                                to={`/specialties/${spec.slug}`}
                                data-testid={`specialty-card-${spec.slug}`}
                                className="group h-full flex flex-col bg-white border border-pine/10 rounded-2xl overflow-hidden card-lift hover:border-pine/30"
                            >
                                <span className="relative block h-44 overflow-hidden">
                                    <img
                                        src={IMG[spec.img]}
                                        alt={spec.imgAlt}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        loading="lazy"
                                    />
                                    <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-widest2 text-pine bg-ivory/95 px-2.5 py-1.5 rounded-md border border-pine/10">
                                        {t("home.text8", "Launch category")}
                                    </span>
                                </span>
                                <span className="flex flex-col flex-1 p-7">
                                    <span className="flex items-center gap-3">
                                        <Icon
                                            className="w-5 h-5 text-forest shrink-0"
                                            strokeWidth={1.5}
                                        />
                                        <span className="font-serif font-medium text-[1.55rem] leading-tight text-forest">
                                            {spec.name}
                                        </span>
                                    </span>
                                    <span className="block mt-2 font-serif italic text-lg text-pine/80">
                                        {spec.tagline}
                                    </span>
                                    <span className="block mt-2 text-sm leading-relaxed text-moss flex-1">
                                        {spec.desc}
                                    </span>
                                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-forest">
                                        {t("home.text9", "View specialty")}
                                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                    </span>
                                </span>
                            </Link>
                        </Reveal>
                    );
                })}
            </div>
            <Reveal delay={0.15}>
                <div className="mt-12">
                    <Link
                        to="/specialties"
                        data-testid="specialties-cta"
                        className="btn-secondary group"
                    >
                        {t("home.specialties.cta", "Explore Specialties")}
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </div>
            </Reveal>
        </div>
    </section>

    );
};

const WhyKerala = () => {
    const { t, tHtml, site: siteData } = useLang();
    const { WHY_KERALA_POINTS } = siteData;
    return (
    <section className="section-pad bg-ivory" data-testid="why-kerala-section">
        <div className="container-site">
            <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
                <div className="lg:sticky lg:top-28">
                    <SectionHeading
                        index="05"
                        eyebrow={t("home.whyKerala.eyebrow", "Why Kerala — God's Own Country")}
                        title={tHtml("home.whyKerala.title", <>
                            Exceptional medical care in an{" "}
                            <span className="italic text-pine">extraordinary</span>{" "}
                            {t("home.text10", "place.")}
                        </>)}
                        intro={t("home.whyKerala.intro", "Kerala, God's Own Country, combines advanced healthcare, experienced medical professionals and warm hospitality within a peaceful and naturally beautiful environment.")}
                    />
                    <Reveal delay={0.2}>
                        <div className="mt-10 rounded-2xl overflow-hidden h-64 sm:h-80 shadow-[0_20px_50px_rgba(6,60,53,0.12)] relative">
                            <img
                                src={IMG.keralaMunnar}
                                alt="Golden sunrise over mist-covered tea plantations in the Kerala highlands"
                                data-testid="why-kerala-image"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                            <p
                                className="absolute bottom-4 left-5 right-5 font-serif italic text-xl text-ivory drop-shadow-[0_2px_8px_rgba(6,60,53,0.7)]"
                                data-testid="why-kerala-statement"
                            >
                                {t("home.keralaQuote", '"Where advanced medicine meets timeless beauty."')}
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <Link
                            to="/why-kerala"
                            data-testid="why-kerala-cta"
                            className="btn-secondary mt-10 group"
                        >
                            {t("home.whyKerala.cta", "Discover Kerala")}
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
                <div>
                    {WHY_KERALA_POINTS.map((point, i) => (
                        <Reveal key={point.title} delay={i * 0.05}>
                            <div
                                data-testid={`why-kerala-point-${i + 1}`}
                                className="border-t border-pine/15 py-6 grid grid-cols-[2.5rem_1fr] gap-4 sm:gap-8"
                            >
                                <span className="font-serif italic text-xl text-natural leading-none pt-1">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <div>
                                    <h3 className="font-serif font-medium text-2xl text-forest leading-snug">
                                        {point.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-moss">
                                        {point.desc}
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>

            {/* modern infrastructure duo */}
            <div className="mt-16 grid lg:grid-cols-2 gap-6 items-stretch">
                <Reveal className="h-full">
                    <div className="relative h-full min-h-[280px] rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(6,60,53,0.12)]">
                        <img
                            src={IMG.hospitalExterior}
                            alt="Modern hospital building with a glass facade and tropical landscaping in Kerala"
                            data-testid="kerala-infra-exterior"
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                </Reveal>
                <Reveal delay={0.1} className="h-full">
                    <div className="h-full min-h-[280px] bg-forest text-ivory rounded-2xl p-8 sm:p-10 flex flex-col justify-center">
                        <LeafMark className="w-7 h-7 text-natural" />
                        <h3 className="mt-5 font-serif font-medium text-2xl sm:text-3xl leading-snug">
                            Beautiful Kerala.{" "}
                            <span className="italic text-natural">
                                {t("home.text11", "Sophisticated modern healthcare.")}
                            </span>
                        </h3>
                        <p className="mt-4 text-sm leading-relaxed text-ivory/65 max-w-md">
                            {t("home.text12", "Behind the greenery stands serious medical infrastructure — advanced diagnostics, modern theatres and internationally experienced clinical teams.")}
                        </p>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>

    );
};

const YourJourney = () => {
    const { t, tHtml } = useLang();
    return (
    <section className="section-pad bg-white" data-testid="journey-section">
        <div className="container-site">
            <SectionHeading
                index="06"
                eyebrow={t("home.journey.eyebrow", "Your Journey")}
                title={tHtml("home.journey.title", <>
                    From first enquiry to Kerala —{" "}
                    <span className="italic text-pine">and back home.</span>
                </>)}
                intro={t("home.journey.intro", "Seven clear stages, one team coordinating them with you. Scroll the path your care will take.")}
            />
            <div className="mt-16 max-w-5xl mx-auto">
                <JourneyTimeline />
            </div>
        </div>
    </section>

    );
};

const Coordination = () => {
    const { t, tHtml, site: siteData } = useLang();
    const { COORDINATION_GROUPS } = siteData;
    return (
    <section className="section-pad bg-sage/60" data-testid="coordination-section">
        <div className="container-site">
            <SectionHeading
                index="07"
                eyebrow={t("home.coordination.eyebrow", "What We Coordinate")}
                title={t("home.coordination.title", "Support at every stage.")}
                intro={t("home.coordination.intro", "Where a service is delivered by trusted local partners, we coordinate it on your behalf — so you always have a single point of contact.")}
            />
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {COORDINATION_GROUPS.map((group, i) => (
                    <Reveal key={group.title} delay={i * 0.08} className="h-full">
                        <div
                            data-testid={`coordination-group-${i + 1}`}
                            className="h-full bg-white border border-pine/10 rounded-2xl overflow-hidden card-lift"
                        >
                            <div className="h-32 overflow-hidden">
                                <img
                                    src={IMG[group.img]}
                                    alt={group.imgAlt}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                            <div className="p-7">
                                <p className="eyebrow text-forest text-[11px]">
                                    {group.title}
                                </p>
                                <ul className="mt-5 space-y-3">
                                    {group.items.map((item) => (
                                        <li
                                            key={item}
                                            className="flex items-start gap-2.5 text-sm text-charcoal/80 leading-snug"
                                        >
                                            <LeafMark className="w-3 h-3 mt-0.5 text-natural shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>

    );
};

const HospitalsTeaser = () => {
    const { t, tHtml } = useLang();
    return (
    <section className="section-pad bg-ivory" data-testid="hospitals-section">
        <div className="container-site">
            <SectionHeading
                index="08"
                eyebrow={t("home.hospitals.eyebrow", "Hospitals")}
                title={t("home.hospitals.title", "Healthcare providers you can trust.")}
                intro={t("home.hospitals.intro", "We are carefully building a network of partner hospitals and specialty centres across India — starting in Kerala. Each provider is reviewed before it is presented to any patient.")}
            />
            <Reveal delay={0.1}>
                <div
                    data-testid="hospitals-placeholder"
                    className="mt-14 bg-white/70 border border-dashed border-pine/30 rounded-2xl px-8 py-16 sm:py-20 text-center"
                >
                    <p className="font-serif italic text-2xl sm:text-3xl text-forest max-w-xl mx-auto">
                        {t("home.text13", "Verified healthcare partners will be displayed here.")}
                    </p>
                    <p className="mt-4 text-sm text-moss max-w-md mx-auto leading-relaxed">
                        {t("home.text14", "Browse our demonstration directory to see how partner profiles will appear once verification is complete.")}
                    </p>
                    <Link
                        to="/hospitals"
                        data-testid="hospitals-cta"
                        className="btn-secondary mt-8 group"
                    >
                        {t("home.hospitals.cta", "View Hospitals")}
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </div>
            </Reveal>
        </div>
    </section>

    );
};

export const Home = () => {
    const { t } = useLang();
    usePageMeta(
        t("meta.home.title", ""),
        t("meta.home.desc", "UniversHeal connects international patients with world-class healthcare across India — specialist consultations, second opinions, treatment planning and complete journey support. Starting in Kerala, God's Own Country.")
    );
    return (
        <>
            <Hero />
            <Marquee />
            <ThreeWays />
            <Difference />
            <HowItWorks />
            <Specialties />
            <WhyKerala />
            <YourJourney />
            <Coordination />
            <HospitalsTeaser />
            <CtaBand />
        </>
    );
};
